/**
 * SuggestedCorrectionsPanel - Display suggested corrections from the validator agent
 * Grouped by severity (major → moderate → minor) with collapsible sections and AI reasoning
 */

import {
  CorrectionSeverity,
  formatFieldNameForDisplay,
  SuggestedCorrection,
  ValidationMetadata,
} from "@/types/validation";

interface SuggestedCorrectionsPanelProps {
  validationMetadata: ValidationMetadata | null;
  className?: string;
}

interface CorrectionCardProps {
  correction: SuggestedCorrection;
}

/**
 * Convert Python dict/list string format to valid JSON
 * Handles: single quotes → double quotes, None → null, True → true, False → false
 * Preserves embedded double quotes within strings (e.g., dimensions like 30" X 23")
 */
function normalizePythonToJson(pythonStr: string): string {
  let result = "";
  let inString = false;
  let stringChar = "";

  for (let i = 0; i < pythonStr.length; i++) {
    const char = pythonStr[i];
    const prevChar = i > 0 ? pythonStr[i - 1] : "";

    // Track if we're inside a string
    if ((char === "'" || char === '"') && prevChar !== "\\") {
      if (!inString) {
        inString = true;
        stringChar = char;
        // Always output double quote for JSON
        result += '"';
      } else if (char === stringChar) {
        inString = false;
        stringChar = "";
        result += '"';
      } else {
        // It's the other quote type inside a string - escape double quotes for JSON
        if (char === '"') {
          result += '\\"';
        } else {
          result += char;
        }
      }
    } else {
      result += char;
    }
  }

  // Replace Python literals with JSON equivalents (outside strings handled by regex on boundaries)
  result = result
    .replace(/: None(?=[,}\]])/g, ": null")
    .replace(/: True(?=[,}\]])/g, ": true")
    .replace(/: False(?=[,}\]])/g, ": false")
    .replace(/\[None(?=[,\]])/g, "[null")
    .replace(/, None(?=[,\]])/g, ", null");

  return result;
}

/**
 * Format a value for display - handles JSON objects (like shipper/consignee entities)
 * and renders them in a more readable format
 */
function formatValueForDisplay(value: string | null | undefined): React.ReactNode {
  if (!value) {
    return <span className="italic text-slate-400">N/A</span>;
  }

  // Try to parse as JSON to check if it's an entity object
  // Handle both JSON (double quotes) and Python dict format (single quotes)
  try {
    // Convert Python dict/list format to valid JSON
    // This handles: single quotes as delimiters, None, True, False
    // And preserves embedded double quotes in strings (like dimensions: 30" X 23")
    const normalizedValue = normalizePythonToJson(value);
    const parsed = JSON.parse(normalizedValue);

    // Handle arrays (e.g., shipment_details, trace_numbers)
    if (Array.isArray(parsed)) {
      // Check if it looks like shipment_details array
      if (
        parsed.length > 0 &&
        typeof parsed[0] === "object" &&
        ("weight" in parsed[0] || "pallets" in parsed[0] || "description" in parsed[0])
      ) {
        return <ShipmentDetailsDisplay details={parsed} />;
      }
      // Check if it looks like trace_numbers array
      if (
        parsed.length > 0 &&
        typeof parsed[0] === "object" &&
        ("type" in parsed[0] || "value" in parsed[0])
      ) {
        return <TraceNumbersDisplay traces={parsed} />;
      }
      // Generic array display
      return (
        <div className="space-y-1">
          {parsed.slice(0, 5).map((item, idx) => (
            <div key={idx} className="text-xs text-slate-700 dark:text-slate-300">
              {typeof item === "object" ? JSON.stringify(item) : String(item)}
            </div>
          ))}
          {parsed.length > 5 && (
            <span className="text-xs text-slate-400">+{parsed.length - 5} more</span>
          )}
        </div>
      );
    }

    if (typeof parsed === "object" && parsed !== null) {
      // Check if it looks like an address entity (has name, address, city, state, zip_code)
      if ("name" in parsed || "address" in parsed || "city" in parsed) {
        return <EntityDisplay entity={parsed} />;
      }
      // For other objects, display as formatted key-value pairs
      return (
        <div className="space-y-0.5">
          {Object.entries(parsed).map(([key, val]) => (
            <div key={key} className="text-xs">
              <span className="text-slate-500 dark:text-slate-400">
                {formatFieldNameForDisplay(key)}:
              </span>{" "}
              <span className="text-slate-900 dark:text-slate-100">{String(val)}</span>
            </div>
          ))}
        </div>
      );
    }
  } catch {
    // Not valid JSON, display as plain text
  }

  return value;
}

/**
 * Display shipment details line items
 */
function ShipmentDetailsDisplay({ details }: { details: Record<string, unknown>[] }) {
  return (
    <div className="space-y-2">
      {details.slice(0, 5).map((item, idx) => {
        const pieces = item.pieces as number | null | undefined;
        const pallets = item.pallets as number | null | undefined;
        const weight = item.weight as number | null | undefined;
        const actualClass = item.actual_class as string | null | undefined;
        const commodityCode = item.commodity_code as string | null | undefined;
        const description = item.description as string | null | undefined;
        return (
          <div key={idx} className="text-xs border-l-2 border-slate-300 pl-2 py-0.5">
            <div className="flex flex-wrap gap-x-2 text-slate-800 dark:text-slate-200">
              {pallets != null && pallets > 0 && <span>{pallets} plt</span>}
              {pieces != null && pieces > 0 && <span>{pieces} pcs</span>}
              {weight != null && <span className="font-medium">{weight} lbs</span>}
              {actualClass && (
                <span className="text-purple-600 dark:text-purple-400">Class {actualClass}</span>
              )}
              {commodityCode && (
                <span className="font-mono text-blue-600 dark:text-blue-400">
                  NMFC: {commodityCode}
                </span>
              )}
            </div>
            {description && (
              <div className="text-slate-600 dark:text-slate-400 mt-0.5 line-clamp-2">
                {description}
              </div>
            )}
          </div>
        );
      })}
      {details.length > 5 && (
        <span className="text-xs text-slate-400">+{details.length - 5} more items</span>
      )}
    </div>
  );
}

/**
 * Display trace numbers
 */
function TraceNumbersDisplay({ traces }: { traces: Record<string, unknown>[] }) {
  return (
    <div className="space-y-1">
      {traces.slice(0, 5).map((trace, idx) => {
        const type = trace.type as string | null | undefined;
        const traceValue = trace.value as string | null | undefined;
        return (
          <div key={idx} className="text-xs">
            <span className="text-slate-500 dark:text-slate-400 font-medium">{type || "REF"}:</span>{" "}
            <span className="text-slate-800 dark:text-slate-200 font-mono">
              {traceValue || "—"}
            </span>
          </div>
        );
      })}
      {traces.length > 5 && (
        <span className="text-xs text-slate-400">+{traces.length - 5} more</span>
      )}
    </div>
  );
}

/**
 * Display an entity (shipper, consignee, bill_to) in a formatted way
 */
function EntityDisplay({ entity }: { entity: Record<string, unknown> }) {
  const name = entity.name as string | undefined;
  const address = entity.address as string | undefined;
  const city = entity.city as string | undefined;
  const state = entity.state as string | undefined;
  const zipCode = (entity.zip_code || entity.zipCode) as string | undefined;
  const phone = (entity.contact_phone || entity.phone) as string | undefined;

  return (
    <div className="text-sm space-y-0.5">
      {name && <div className="font-semibold text-slate-900 dark:text-slate-100">{name}</div>}
      {address && <div className="text-slate-700 dark:text-slate-300">{address}</div>}
      {(city || state || zipCode) && (
        <div className="text-slate-700 dark:text-slate-300">
          {[city, state].filter(Boolean).join(", ")}

          {zipCode && ` ${zipCode}`}
        </div>
      )}
      {phone && <div className="text-slate-500 dark:text-slate-400 text-xs">📞 {phone}</div>}
    </div>
  );
}

/**
 * Individual correction card with value comparison and AI reasoning
 */
function CorrectionCard({ correction }: CorrectionCardProps) {
  return (
    <div className="space-y-3">
      {/* Header with field name */}
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-slate-800 dark:text-slate-200">
          {formatFieldNameForDisplay(correction.field_name)}
        </span>
        {/* Severity badge */}
        <span
          className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${
            correction.severity === "major"
              ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
              : correction.severity === "moderate"
                ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400"
          }`}
        >
          {correction.suggested_action}
        </span>
      </div>

      {/* Value Comparison - Side by Side */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1">
          <p className="text-[10px] font-bold uppercase text-slate-400">
            <span className="material-symbols-outlined text-xs align-middle mr-1">
              document_scanner
            </span>
            Extracted from BOL
          </p>
          <div className="rounded bg-white p-2 text-xs border border-slate-200 dark:bg-slate-800 dark:border-slate-700 font-medium">
            {formatValueForDisplay(correction.extracted_value)}
          </div>
        </div>
        <div className="space-y-1">
          <p className="text-[10px] font-bold uppercase text-slate-400">
            <span className="material-symbols-outlined text-xs align-middle mr-1">database</span>
            System Record
          </p>
          <div className="rounded bg-white p-2 text-xs border border-slate-200 dark:bg-slate-800 dark:border-slate-700 text-slate-500 italic">
            {formatValueForDisplay(correction.existing_value)}
          </div>
        </div>
      </div>

      {/* AI Reasoning Box */}
      {correction.reasoning && (
        <div
          className={`rounded p-2.5 ${
            correction.severity === "major"
              ? "bg-red-50 dark:bg-red-900/20"
              : correction.severity === "moderate"
                ? "bg-amber-50 dark:bg-amber-900/20"
                : "bg-slate-50 dark:bg-slate-800/50"
          }`}
        >
          <div className="flex gap-2">
            <span
              className={`material-symbols-outlined text-sm ${
                correction.severity === "major"
                  ? "text-red-600"
                  : correction.severity === "moderate"
                    ? "text-amber-600"
                    : "text-slate-600"
              }`}
            >
              auto_awesome
            </span>
            <p
              className={`text-[11px] leading-relaxed ${
                correction.severity === "major"
                  ? "text-red-800 dark:text-red-300"
                  : correction.severity === "moderate"
                    ? "text-amber-800 dark:text-amber-300"
                    : "text-slate-700 dark:text-slate-300"
              }`}
            >
              <b>AI Reasoning:</b> {correction.reasoning}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Collapsible group of corrections by severity using HTML details/summary
 */
function CorrectionGroup({
  severity,
  corrections,
  defaultOpen = false,
}: {
  severity: CorrectionSeverity;
  corrections: SuggestedCorrection[];
  defaultOpen?: boolean;
}) {
  if (corrections.length === 0) return null;

  const severityConfig = {
    major: {
      border: "border-red-200 dark:border-red-900/30",
      bg: "bg-red-50/30 dark:bg-red-900/10",
      text: "text-red-700",
      dot: "bg-red-500",
      label: "Major Issues",
      divider: "border-red-100 dark:border-red-900/30",
    },
    moderate: {
      border: "border-amber-200 dark:border-amber-900/30",
      bg: "bg-amber-50/30 dark:bg-amber-900/10",
      text: "text-amber-700",
      dot: "bg-amber-500",
      label: "Moderate Issues",
      divider: "border-amber-100 dark:border-amber-900/30",
    },
    minor: {
      border: "border-slate-200 dark:border-slate-800",
      bg: "bg-slate-50/30 dark:bg-slate-800/20",
      text: "text-slate-600 dark:text-slate-400",
      dot: "bg-slate-400",
      label: "Minor Issues",
      divider: "border-slate-100 dark:border-slate-800",
    },
  };

  const config = severityConfig[severity];

  return (
    <details className={`group rounded-xl border ${config.border} ${config.bg}`} open={defaultOpen}>
      <summary className="flex cursor-pointer items-center justify-between px-4 py-3 list-none">
        <span
          className={`text-xs font-bold uppercase tracking-tight flex items-center gap-2 ${config.text}`}
        >
          <span className={`size-2 rounded-full ${config.dot}`} />
          {config.label} ({corrections.length})
        </span>
        <span className="material-symbols-outlined text-slate-400 transition-transform group-open:rotate-180">
          expand_more
        </span>
      </summary>

      <div className={`px-4 pb-4 space-y-4 border-t ${config.divider} pt-4`}>
        {corrections.map((correction, idx) => (
          <CorrectionCard key={`${correction.field_name}-${idx}`} correction={correction} />
        ))}
      </div>
    </details>
  );
}

/**
 * Validation Checks Display - Pill-shaped badges for each check
 */
function ValidationChecksDisplay({ checks }: { checks: Record<string, boolean> }) {
  if (!checks || Object.keys(checks).length === 0) return null;

  return (
    <div className="space-y-3">
      <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
        Validation Checks
      </h3>
      <div className="flex flex-wrap gap-2">
        {Object.entries(checks).map(([check, passed]) => (
          <div
            key={check}
            className={`flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold ${
              passed
                ? "border-green-200 bg-green-50 text-green-700 dark:bg-green-900/20 dark:border-green-800 dark:text-green-400"
                : "border-red-200 bg-red-50 text-red-700 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400"
            }`}
          >
            <span className="material-symbols-outlined text-xs">{passed ? "check" : "close"}</span>
            {formatFieldNameForDisplay(check)}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Main panel component for displaying suggested corrections
 */
export function SuggestedCorrectionsPanel({
  validationMetadata,
  className = "",
}: SuggestedCorrectionsPanelProps) {
  if (!validationMetadata || validationMetadata.suggested_corrections.length === 0) {
    return null;
  }

  const corrections = validationMetadata.suggested_corrections;

  // Group by severity
  const grouped = {
    major: corrections.filter((c) => c.severity === "major"),
    moderate: corrections.filter((c) => c.severity === "moderate"),
    minor: corrections.filter((c) => c.severity === "minor"),
  };

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-sm font-bold text-slate-900 dark:text-white">
          <span className="material-symbols-outlined text-amber-500">warning</span>
          Suggested Corrections ({corrections.length})
        </h3>
      </div>

      {/* Correction Groups - Collapsible by severity */}
      <CorrectionGroup severity="major" corrections={grouped.major} defaultOpen={true} />
      <CorrectionGroup severity="moderate" corrections={grouped.moderate} />
      <CorrectionGroup severity="minor" corrections={grouped.minor} />

      {/* Validation Checks */}
      {validationMetadata.validation_checks && (
        <ValidationChecksDisplay checks={validationMetadata.validation_checks} />
      )}
    </div>
  );
}

export default SuggestedCorrectionsPanel;
